import { forwardRef } from 'react'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'
import { Card, CardContent } from '@/components/ui/card'
import { Building2, User } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

interface KanbanCardProps extends React.HTMLAttributes<HTMLDivElement> {
  opportunity: CrmFundraisingOpportunity
  isDragging?: boolean
}

export const KanbanCard = forwardRef<HTMLDivElement, KanbanCardProps>(
  ({ opportunity, isDragging, className, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        className={`group relative overflow-hidden py-0 gap-0 border transition-all duration-200 ${
          isDragging
            ? 'shadow-xl ring-2 ring-primary/30 scale-[1.02] rotate-1'
            : 'hover:shadow-md hover:border-foreground/15'
        } ${className ?? ''}`}
        {...props}
      >
        <CardContent className="px-3 py-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 space-y-1.5">
              {/* Name */}
              <p
                className="text-xs font-semibold leading-snug line-clamp-2 text-foreground"
                title={opportunity.name}
              >
                {opportunity.name}
              </p>

              {/* Donor */}
              {opportunity.donor_name && (
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  {opportunity.donor_type?.includes('OrganizationDonor') ? (
                    <Building2 className="h-2.5 w-2.5 shrink-0" />
                  ) : (
                    <User className="h-2.5 w-2.5 shrink-0" />
                  )}
                  <span className="truncate">{opportunity.donor_name}</span>
                </div>
              )}

              {/* Amount */}
              <p className="text-[11px] font-medium text-muted-foreground tabular-nums">
                {opportunity.estimated_amount_formatted}
              </p>

              {/* Assignee */}
              {opportunity.assignee_name && (
                <div className="flex items-center gap-1.5 pt-0.5">
                  <Avatar className="h-4.5 w-4.5 text-[8px]">
                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                      {getInitials(opportunity.assignee_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-[10px] text-muted-foreground truncate">
                    {opportunity.assignee_name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }
)

KanbanCard.displayName = 'KanbanCard'
